/**
 * Trusscore Estimator - Material Consolidation
 *
 * Aggregates materials from all surfaces into a consolidated Bill of Materials.
 * Handles deduplication by product ID and calculates totals.
 *
 * Ported from: consolidateMaterialsFromSurfaces() in estimator.js
 */

import {
  MaterialItem,
  BillOfMaterials,
  ScrewRequirement,
  CostTotals,
  Currency,
  FullRoomWallConfig,
  RoomDimensions,
  BOMSmoothingConfig,
  BOMSmoothingResult,
} from '../types';
import { findProductById } from '../data/productTable';
import { calculateScrewsFromBOM } from './screws';
import { CeilingResult } from './ceiling';
import { WallResult, aggregateWallResults } from './walls';
import { smoothBillOfMaterials } from './smoothing';

/**
 * Configuration for FullRoom mode legacy J-trim calculation.
 */
export interface FullRoomTrimConfig {
  roomDimensions: RoomDimensions;
  fullRoomWallConfig: FullRoomWallConfig;
}

/**
 * Consolidate material items by product ID.
 *
 * Items with the same productID have their quantities summed.
 *
 * @param items - Array of material items (may have duplicates)
 * @returns Consolidated array with unique product IDs
 */
export function consolidateMaterialItems(items: MaterialItem[]): MaterialItem[] {
  const consolidated = new Map<string, MaterialItem>();

  for (const item of items) {
    const existing = consolidated.get(item.productID);

    if (existing) {
      // Sum quantities
      existing.quantity += item.quantity;
    } else {
      // Create new entry (copy to avoid mutation)
      consolidated.set(item.productID, { ...item });
    }
  }

  return Array.from(consolidated.values());
}

/**
 * Categorize material items by type prefix.
 *
 * @param items - Array of material items
 * @param typePrefix - Type prefix to filter (e.g., 'WCB', 'H_Trim')
 * @returns Filtered items matching the type prefix
 */
export function filterByTypePrefix(items: MaterialItem[], typePrefix: string): MaterialItem[] {
  return items.filter(item => item.type.startsWith(typePrefix));
}

/**
 * Create empty Bill of Materials.
 */
export function createEmptyBOM(): BillOfMaterials {
  return {
    wallAndCeilingBoard: [],
    slatwall: [],
    hTrims: [],
    jTrimsSnapIn: [],
    jTrimsHalfInch: [],
    jTrimsThreeQuarterInch: [],
    screws: {
      trimScrews: 0,
      panelScrews: 0,
      total: 0,
    },
  };
}

/**
 * Build Bill of Materials from ceiling and wall results.
 *
 * @param ceilingResult - Ceiling calculation result (optional)
 * @param wallResults - Array of wall calculation results
 * @param fullRoomConfig - Optional FullRoom config (no longer used for J-trim override)
 * @param smoothingConfig - Optional BOM smoothing configuration
 * @returns Consolidated Bill of Materials (with optional smoothing applied)
 */
export function buildBillOfMaterials(
  ceilingResult: CeilingResult | null,
  wallResults: WallResult[],
  fullRoomConfig?: FullRoomTrimConfig,
  smoothingConfig?: BOMSmoothingConfig
): BillOfMaterials {
  // Build raw BOM first
  const rawBOM = buildRawBillOfMaterials(ceilingResult, wallResults);

  // Apply smoothing if enabled
  if (smoothingConfig?.enabled) {
    const smoothingResult = smoothBillOfMaterials(rawBOM, smoothingConfig);
    return smoothingResult.smoothedBOM;
  }

  return rawBOM;
}

/**
 * Build Bill of Materials with full smoothing details.
 *
 * Use this variant when you need access to the smoothing result
 * (substitutions made, waste impact, etc.).
 *
 * @param ceilingResult - Ceiling calculation result (optional)
 * @param wallResults - Array of wall calculation results
 * @param fullRoomConfig - Optional FullRoom config
 * @param smoothingConfig - Optional BOM smoothing configuration
 * @returns Object containing BOM and optional smoothing result
 */
export function buildBillOfMaterialsWithSmoothing(
  ceilingResult: CeilingResult | null,
  wallResults: WallResult[],
  fullRoomConfig?: FullRoomTrimConfig,
  smoothingConfig?: BOMSmoothingConfig
): { bom: BillOfMaterials; smoothingResult?: BOMSmoothingResult } {
  // Build raw BOM first
  const rawBOM = buildRawBillOfMaterials(ceilingResult, wallResults);

  // Apply smoothing if enabled
  if (smoothingConfig?.enabled) {
    const smoothingResult = smoothBillOfMaterials(rawBOM, smoothingConfig);
    return { bom: smoothingResult.smoothedBOM, smoothingResult };
  }

  return { bom: rawBOM };
}

/**
 * Build raw Bill of Materials without smoothing.
 * Internal function used by buildBillOfMaterials and buildBillOfMaterialsWithSmoothing.
 */
function buildRawBillOfMaterials(
  ceilingResult: CeilingResult | null,
  wallResults: WallResult[]
): BillOfMaterials {
  // Collect all materials
  const allWCBPanels: MaterialItem[] = [];
  const allSlatWallPanels: MaterialItem[] = [];
  const allHTrims: MaterialItem[] = [];
  const allJTrimsSnapIn: MaterialItem[] = [];
  const allJTrimsHalf: MaterialItem[] = [];
  const allJTrimsThreeQuarter: MaterialItem[] = [];

  // Add ceiling materials. CeilingResult.jTrims now holds Snap-In items.
  if (ceilingResult) {
    allWCBPanels.push(...ceilingResult.panels);
    allHTrims.push(...ceilingResult.hTrims);
    allJTrimsSnapIn.push(...ceilingResult.jTrims);
  }

  // Aggregate wall materials
  const aggregatedWalls = aggregateWallResults(wallResults);

  allWCBPanels.push(...aggregatedWalls.panels);
  allSlatWallPanels.push(...aggregatedWalls.slatWallPanels);
  allHTrims.push(...aggregatedWalls.hTrims);

  // Snap-In is produced by all current calc paths; legacy buckets stay empty for new
  // estimates and are kept only so consumers reading the old fields still type-check.
  allJTrimsSnapIn.push(...aggregatedWalls.jTrimsSnapIn);
  allJTrimsHalf.push(...aggregatedWalls.jTrimsHalfInch);
  allJTrimsThreeQuarter.push(...aggregatedWalls.jTrimsThreeQuarter);

  // Consolidate each category
  const bom: BillOfMaterials = {
    wallAndCeilingBoard: consolidateMaterialItems(allWCBPanels),
    slatwall: consolidateMaterialItems(allSlatWallPanels),
    hTrims: consolidateMaterialItems(allHTrims),
    jTrimsSnapIn: consolidateMaterialItems(allJTrimsSnapIn),
    jTrimsHalfInch: consolidateMaterialItems(allJTrimsHalf),
    jTrimsThreeQuarterInch: consolidateMaterialItems(allJTrimsThreeQuarter),
    screws: {
      trimScrews: 0,
      panelScrews: 0,
      total: 0,
    },
  };

  // Calculate screws
  bom.screws = calculateScrewsFromBOM(bom);

  return bom;
}

/**
 * Calculate cost totals for a Bill of Materials.
 *
 * @param bom - Bill of Materials
 * @returns Cost totals in USD and CAD
 */
export function calculateCostTotals(bom: BillOfMaterials): CostTotals {
  let totalUSD = 0;
  let totalCAD = 0;

  const itemizedCosts = {
    wallAndCeilingBoard: { usd: 0, cad: 0 },
    slatwall: { usd: 0, cad: 0 },
    trims: { usd: 0, cad: 0 },
    screws: { usd: 0, cad: 0 },
  };

  // Helper to calculate costs for material items
  const calculateItemCosts = (items: MaterialItem[]): { usd: number; cad: number } => {
    let usd = 0;
    let cad = 0;

    for (const item of items) {
      const product = findProductById(item.productID);
      if (product) {
        usd += item.quantity * product.pricingUSD;
        cad += item.quantity * product.pricingCAD;
      }
    }

    return { usd, cad };
  };

  // W&CB costs
  const wcbCosts = calculateItemCosts(bom.wallAndCeilingBoard);
  itemizedCosts.wallAndCeilingBoard = wcbCosts;
  totalUSD += wcbCosts.usd;
  totalCAD += wcbCosts.cad;

  // SlatWall costs
  const swCosts = calculateItemCosts(bom.slatwall);
  itemizedCosts.slatwall = swCosts;
  totalUSD += swCosts.usd;
  totalCAD += swCosts.cad;

  // Trim costs (all trim types combined). Legacy estimates have jTrimsHalfInch /
  // jTrimsThreeQuarterInch populated; new estimates use jTrimsSnapIn. Both contribute.
  const hTrimCosts = calculateItemCosts(bom.hTrims);
  const jTrimSnapInCosts = calculateItemCosts(bom.jTrimsSnapIn ?? []);
  const jTrimHalfCosts = calculateItemCosts(bom.jTrimsHalfInch);
  const jTrimThreeQuarterCosts = calculateItemCosts(bom.jTrimsThreeQuarterInch);

  itemizedCosts.trims = {
    usd: hTrimCosts.usd + jTrimSnapInCosts.usd + jTrimHalfCosts.usd + jTrimThreeQuarterCosts.usd,
    cad: hTrimCosts.cad + jTrimSnapInCosts.cad + jTrimHalfCosts.cad + jTrimThreeQuarterCosts.cad,
  };
  totalUSD += itemizedCosts.trims.usd;
  totalCAD += itemizedCosts.trims.cad;

  // Screw costs (not in product table yet, so zero)
  // Could be added in the future with product lookup

  return {
    totalCostUSD: Math.round(totalUSD * 100) / 100,
    totalCostCAD: Math.round(totalCAD * 100) / 100,
    itemizedCosts,
  };
}

/**
 * Format currency value for display.
 *
 * @param amount - Numeric amount
 * @param currency - Currency type
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: Currency): string {
  const symbol = currency === 'USD' ? '$' : 'C$';
  return `${symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Get total item count from BOM.
 *
 * @param bom - Bill of Materials
 * @returns Total number of items (panels + trims)
 */
export function getTotalItemCount(bom: BillOfMaterials): number {
  const wcbCount = bom.wallAndCeilingBoard.reduce((sum, item) => sum + item.quantity, 0);
  const swCount = bom.slatwall.reduce((sum, item) => sum + item.quantity, 0);
  const hTrimCount = bom.hTrims.reduce((sum, item) => sum + item.quantity, 0);
  const jTrimSnapInCount = (bom.jTrimsSnapIn ?? []).reduce((sum, item) => sum + item.quantity, 0);
  const jTrimHalfCount = bom.jTrimsHalfInch.reduce((sum, item) => sum + item.quantity, 0);
  const jTrimThreeQuarterCount = bom.jTrimsThreeQuarterInch.reduce((sum, item) => sum + item.quantity, 0);

  return wcbCount + swCount + hTrimCount + jTrimSnapInCount + jTrimHalfCount + jTrimThreeQuarterCount;
}

/**
 * Get total square footage from BOM.
 *
 * @param bom - Bill of Materials
 * @returns Total square footage of panels
 */
export function getTotalSquareFootage(bom: BillOfMaterials): number {
  const wcbSqft = bom.wallAndCeilingBoard.reduce(
    (sum, item) => sum + item.quantity * item.sqft,
    0
  );
  const swSqft = bom.slatwall.reduce(
    (sum, item) => sum + item.quantity * item.sqft,
    0
  );

  return Math.round(wcbSqft + swSqft);
}

/**
 * Generate BOM summary for display.
 *
 * @param bom - Bill of Materials
 * @returns Summary object with counts and totals
 */
export function generateBOMSummary(bom: BillOfMaterials): {
  panelCount: number;
  trimCount: number;
  totalSqft: number;
  screwCount: number;
  uniqueProducts: number;
} {
  const panelCount =
    bom.wallAndCeilingBoard.reduce((sum, item) => sum + item.quantity, 0) +
    bom.slatwall.reduce((sum, item) => sum + item.quantity, 0);

  const trimCount =
    bom.hTrims.reduce((sum, item) => sum + item.quantity, 0) +
    (bom.jTrimsSnapIn ?? []).reduce((sum, item) => sum + item.quantity, 0) +
    bom.jTrimsHalfInch.reduce((sum, item) => sum + item.quantity, 0) +
    bom.jTrimsThreeQuarterInch.reduce((sum, item) => sum + item.quantity, 0);

  const totalSqft = getTotalSquareFootage(bom);
  const screwCount = bom.screws.total;

  const uniqueProducts =
    bom.wallAndCeilingBoard.length +
    bom.slatwall.length +
    bom.hTrims.length +
    (bom.jTrimsSnapIn?.length ?? 0) +
    bom.jTrimsHalfInch.length +
    bom.jTrimsThreeQuarterInch.length;

  return {
    panelCount,
    trimCount,
    totalSqft,
    screwCount,
    uniqueProducts,
  };
}

export default {
  consolidateMaterialItems,
  filterByTypePrefix,
  createEmptyBOM,
  buildBillOfMaterials,
  buildBillOfMaterialsWithSmoothing,
  calculateCostTotals,
  formatCurrency,
  getTotalItemCount,
  getTotalSquareFootage,
  generateBOMSummary,
};
